/**
 * The HUD's pure model, shared by BOTH shells (web apps/client src/ui/hud + overlays, native apps/mobile src/ui/hud +
 * overlays): the snap-hint budgets, stamina segments / "NOT READY" copy, the intro curtain copy, the scorebug's drive
 * copy, the net modal's copy from the server's verdict, the stall countdown, rematch toasts and the prompt-device
 * rules. No UI framework: unit-tested under Node (apps/mobile src/__tests__/hud-model.test.ts).
 */
import { DEVICE_ID, padIndexOf, type InputManager } from '../input/types';
import { LESSONS } from './tutorial/catalog';
import type { MatchSetup } from './types';
import { MatchFormat, POWER, STAMINA, characterById, isCharacterId, type PlayerIndex } from '@hf/shared';
import { Phase } from '@hf/sim';

// ── hints ──

/** How many line-ups the general control reminders stay up (per side), and after an OFFSIDE by a human. */
export const HINT_BUDGET = { offense: 6, defense: 3, afterOffside: 2 } as const;

export interface HintTrack {
  phase: number;
  /** Line-ups the human played on offense / on defense so far. */
  off: number;
  def: number;
  /** Defense line-up number up to which the hint is forced back on (an OFFSIDE by a human defender). */
  boostUntil: number;
  /** Defense line-up during which the human went offside (-1: none yet): the next one gets the red OFFSIDE tag. */
  offsideAt: number;
}

export const newHintTrack = (): HintTrack => ({ phase: -1, off: 0, def: 0, boostUntil: -1, offsideAt: -9 });

/** Count a line-up (entering PRE_SNAP) for the side(s) the human plays (idempotent per phase; kickoffs excluded). */
export const trackLineup = (
  t: HintTrack,
  phase: number,
  role: { offense: boolean; defense: boolean; kickoff: boolean },
): void => {
  if (phase === t.phase) return;
  if (phase === Phase.PRE_SNAP && t.phase !== Phase.PRE_SNAP && !role.kickoff) {
    if (role.offense) t.off++;
    if (role.defense) t.def++;
  }
  t.phase = phase;
};

export const noteHumanOffside = (t: HintTrack): void => {
  t.offsideAt = t.def;
  t.boostUntil = t.def + HINT_BUDGET.afterOffside;
};

export const showDefenseHint = (t: HintTrack): boolean =>
  t.def <= HINT_BUDGET.defense || t.def <= t.boostUntil;
export const showOffenseHint = (t: HintTrack): boolean => t.off <= HINT_BUDGET.offense;

// ── meters ──

export const STAMINA_SEGMENTS = 8;

/** Lit segments for a stamina value (a partly filled segment counts once at least half full). */
export const staminaSegments = (stamina: number): number =>
  Math.max(0, Math.min(STAMINA_SEGMENTS, Math.round((stamina / STAMINA.max) * STAMINA_SEGMENTS)));

/**
 * May side `side`'s SPECIAL-ready call to action show on THIS device (the web's "<NAME> READY!" banner with the Special
 * key, the meter's FULL glow and fill flash; the phone badge's pink ready ring)? Only for a side played here. An online
 * opponent's charged special is his prompt, not ours: both peers run the same sim, so before this every client lit the
 * remote player's ready UI as if it were its own. His meter still shows its fill, and the moment he FIRES it everyone
 * sees it (the "<NAME>!" telegraph call, the running-special banner, the aura on his head).
 */
export const specialReadyVisible = (
  setup: Pick<MatchSetup, 'slots'> | null | undefined,
  side: number,
): boolean => (side === 0 || side === 1 ? setup?.slots[side]?.kind !== 'remote' : false);

/** Why SPECIAL did nothing: the meter is not full yet, or the special was already spent this play. */
export const deniedText = (power: number, ready: boolean): string =>
  ready || power >= POWER.max ? 'NEXT PLAY' : `NOT READY · ${Math.floor(power)}%`;

// ── intro ──

export const introCopy = (
  setup: Pick<MatchSetup, 'mode' | 'config'> | null,
  lessonId: string | null,
): { kicker: string; title: string; go: string; countdown?: string } => {
  const [c0, c1] = setup?.config.characters ?? ['rookie', 'bigtex'];
  const name = (c: string): string => (isCharacterId(c) ? characterById(c).name : c.toUpperCase());
  if (setup?.mode === 'tutorial') {
    const l = LESSONS.find((x) => x.id === lessonId);
    return {
      kicker: l ? `FOOTBALL 101 · LESSON ${l.number} · ${l.title}` : 'FOOTBALL 101',
      title: 'PRACTICE FIELD',
      go: "LET'S GO!",
    };
  }
  if (setup?.mode === 'practice')
    return { kicker: `PRACTICE · ${name(c0)} · INFINITE DOWNS`, title: 'PRACTICE FIELD', go: "LET'S GO!" };
  // Only Classic really kicks off; a Drives game opens on a line-up at the 40 (QA r4: "KICKOFF!" promised a kick
  // the newcomer never saw, and was never taught).
  const classic = setup?.config.format === MatchFormat.CLASSIC;
  return {
    kicker: `${name(c0)} · VS · ${name(c1)}`,
    title: 'TAKING THE FIELD',
    go: classic ? 'KICKOFF!' : "LET'S GO!",
    countdown: classic ? 'KICKOFF IN' : 'STARTING IN',
  };
};

export const kickoffIn = (endsAt: number, now: number): number =>
  Math.max(0, Math.ceil((endsAt - now) / 1000));

// ── scorebug ──

/** "YOUR DRIVE" for the one local human, "CPU DRIVE" / "OPP DRIVE" for the other side, else the team label. */
export const driveOwner = (setup: MatchSetup | null, side: 0 | 1, label: string): string => {
  const kinds = setup ? [setup.slots[0].kind, setup.slots[1].kind] : null;
  const humans = kinds ? kinds.filter((x) => x === 'human').length : 0;
  const kind = kinds?.[side];
  if (humans === 1 && kind === 'human') return 'YOUR DRIVE';
  if (kind === 'cpu') return 'CPU DRIVE';
  if (kind === 'remote') return 'OPP DRIVE';
  return `${label} DRIVE`;
};

export const drivesLeftChip = (n: number): string =>
  n <= 0 ? 'LAST DRIVE' : `${n} DRIVE${n === 1 ? '' : 'S'} LEFT`;

// ── toasts ──

export const isRematchToast = (text: string): boolean => /\brematch\b/i.test(text);

// ── net ──

/** What the server told us about the current match's end (raw lobby messages). */
export interface NetVerdict {
  matchId: string;
  /** opponent_left.reason ('quit' | 'disconnect' | …). */
  left?: string;
  result?: { status: string; winner: number; rating?: { before: number; after: number; delta: number } };
}

export interface NetModalCopy {
  kind: 'lost' | 'win' | 'dropped' | 'desynced' | 'checking';
  title: string;
  /** Small line above the title ("OPPONENT FORFEITED"). */
  kicker?: string;
  body: string;
  tone: 'red' | 'gold' | 'win';
  /** Rating change to show (ranked forfeit wins). */
  rating?: { before: number; after: number; delta: number };
  /** The server has not ruled yet: a "waiting for the server's call" line (the copy updates when it does). */
  pending?: boolean;
  button: string;
}

export const NET_LOST: NetModalCopy = {
  kind: 'lost',
  title: 'CONNECTION LOST',
  body: 'The connection to your opponent timed out, so the match could not continue.',
  tone: 'red',
  button: 'BACK TO MENU',
};
const DESYNCED: NetModalCopy = {
  kind: 'desynced',
  title: 'CONNECTION DESYNCED',
  body: 'The two games stopped agreeing on what happened. The match was stopped so nobody loses unfairly. A report was written to the console.',
  tone: 'gold',
  button: 'BACK TO MENU',
};

/**
 * Our lobby socket is fine but the PEER went silent (the rollback session timed out): the server probes the
 * opponent and rules a few seconds later — a forfeit win for us if they are gone. Saying "the match could not
 * continue" here read as a no-contest, and the win then arrived as a toast in the lobby (QA r4).
 */
const CHECKING: NetModalCopy = {
  kind: 'checking',
  kicker: 'OPPONENT NOT RESPONDING',
  title: 'CONNECTION LOST',
  body: "Your opponent's game stopped answering. The server is checking on them: if they are gone, the win is yours.",
  tone: 'gold',
  pending: true,
  button: 'BACK TO MENU',
};

/**
 * Copy for the 'disconnected' / 'desynced' modal from what the server said about this match. Pure (tested).
 * `you` = our player index online; `socketUp` = our own lobby connection is healthy (so the silence is the
 * opponent's, and the server's verdict is on its way).
 */
export const netModalCopy = (
  overlay: 'disconnected' | 'desynced',
  verdict: NetVerdict | null,
  matchId: string | null,
  you: PlayerIndex | null,
  socketUp = false,
): NetModalCopy => {
  if (overlay === 'desynced') return DESYNCED;
  const v = verdict && matchId && verdict.matchId === matchId ? verdict : null;
  if (!v) return socketUp ? CHECKING : NET_LOST;
  const r = v.result;
  const wonByVerdict = !!r && you !== null && r.winner === you;
  const lostByVerdict = !!r && r.winner !== -1 && r.winner !== you;
  const rating = wonByVerdict && r?.rating ? { rating: r.rating } : {};
  // A forfeit verdict for us, or the opponent QUIT (and no verdict says otherwise): a win, not an error.
  const win =
    (wonByVerdict && (r?.status === 'forfeit' || !!v.left)) || (v.left === 'quit' && !lostByVerdict);
  if (win)
    return {
      kind: 'win',
      kicker: v.left === 'disconnect' ? 'OPPONENT DISCONNECTED' : 'OPPONENT FORFEITED',
      title: 'YOU WIN!',
      // A closed tab is ruled at once (no wait): "could not come back" claimed a wait that never happened (QA r4).
      body:
        v.left === 'disconnect'
          ? "Your opponent's connection closed and the match was called. The win goes to you."
          : 'Your opponent left the match. The win goes to you.',
      tone: 'win',
      ...rating,
      button: 'CONTINUE',
    };
  if (v.left)
    return {
      kind: 'dropped',
      title: 'OPPONENT DISCONNECTED',
      body: r
        ? 'Your opponent lost their connection. The match is over.'
        : 'Your opponent lost their connection. Checking the result with the server…',
      tone: 'gold',
      ...(r ? {} : { pending: true }),
      button: 'BACK TO MENU',
    };
  if (r && r.winner === -1)
    return {
      kind: 'lost',
      title: 'NO CONTEST',
      body: 'The connection between you broke. Nobody wins and no rating changes.',
      tone: 'gold',
      button: 'BACK TO MENU',
    };
  return NET_LOST;
};

/** Whole seconds left before a stalled match is called off (≥ 1 while the strip shows). */
export const stallSecondsLeft = (since: number, timeoutMs: number, now: number): number =>
  Math.max(1, Math.ceil((since + timeoutMs - now) / 1000));

// ── devices ──

/**
 * Device whose labels a slot's prompts use: the last active one if it drives the slot, else the slot's first device
 * (a `pad:*` slot owns every pad not assigned to the other slot). Same rule as the web.
 */
export const promptDevice = (
  setup: MatchSetup | null,
  slot: 0 | 1,
  lastActive: string | null,
  touchVisible = false,
): string | null => {
  const s = setup?.slots[slot];
  if (!s || s.kind !== 'human') return null;
  const humans = setup.slots.filter((x) => x.kind === 'human').length;
  const ids = s.deviceIds ?? [humans === 1 ? 'kb:solo' : slot === 0 ? 'kb:p1' : 'kb:p2'];
  if (lastActive) {
    if (ids.includes(lastActive)) return lastActive;
    if (padIndexOf(lastActive) >= 0 && ids.includes(DEVICE_ID.ANY_PAD)) {
      const other = setup.slots[slot === 0 ? 1 : 0];
      const taken = other?.kind === 'human' && (other.deviceIds ?? []).includes(lastActive);
      if (!taken) return lastActive;
    }
  }
  // the thumbs: the on-screen pad is up and nothing else was used yet
  if (
    touchVisible &&
    (lastActive === null || lastActive === DEVICE_ID.TOUCH) &&
    ids.includes(DEVICE_ID.TOUCH)
  )
    return DEVICE_ID.TOUCH;
  // never label with the wildcard itself: its first concrete sibling (the keyboard) is the honest fallback
  return ids.find((d) => d !== DEVICE_ID.ANY_PAD) ?? ids[0] ?? null;
};

/**
 * Football 101's device (the web's tutorial `pickDevice`): the last device touched if it drives slot 0, the thumbs
 * while the on-screen pad is up and nothing else was used, else the slot's first device.
 */
export const pickDevice = (
  input: Pick<InputManager, 'assigned' | 'lastActiveDevice'> & Partial<Pick<InputManager, 'touch'>>,
  touchVisible: boolean,
): string => {
  let assigned: readonly string[] = [];
  try {
    assigned = input.assigned(0);
  } catch {
    assigned = [];
  }
  const last = input.lastActiveDevice?.() ?? null;
  if ((last === null || last === DEVICE_ID.TOUCH) && touchVisible && assigned.includes(DEVICE_ID.TOUCH))
    return DEVICE_ID.TOUCH;
  const usable =
    last !== null &&
    (assigned.includes(last) || (padIndexOf(last) >= 0 && assigned.includes(DEVICE_ID.ANY_PAD)));
  return usable ? last : (assigned[0] ?? DEVICE_ID.KB_SOLO);
};
